import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { OrdersTableMode } from "../OrdersTableMode";
import { LimitOrder } from "./LimitOrder";
import { RowDataTransaction } from "ag-grid-community/dist/lib/rowModels/clientSide/clientSideRowModel";

export type AggregateServiceContext = {
  has: (id: string) => boolean;
  get: (id: string) => LimitOrder | LadderAggregate;
  add: (id: string) => void;
  /**
   * @param id aggregateId of LadderAggregate or orderId of LimitOrder updateAggregate
   */
  update: (id: string) => void;
  remove: (id: string) => void;
  convert: (transaction: AggregateTransaction, isAdd: boolean, id: string) => RowDataTransaction;
};

export type AggregateTransaction = {
  add: Map<string, LadderAggregate | LimitOrder>;
  update: Map<string, LadderAggregate | LimitOrder>;
  remove: Map<string, LadderAggregate | LimitOrder>;
};

export class LadderAggregate {
  public aggregateOrderIdSet: Set<string> = new Set();

  public readonly isLadderAggregate = true;
  public isAggregated = false;

  public tempStale = true;
  public executedQty = null;
  public leavesQty = null;
  public orderQty = null;
  public executedPerc = null;
  public usdEquivExecutedQty = null;
  public usdEquivQty = null;
  public usdEquivLeavesQty = null;

  // public aggregationPositionQty: CubiqTS.BigNumber;
  // TODO use old name, if different
  public aggregateExpanded = false;

  /**
   * lowest order id in aggregate.
   */
  public aggregateOrderId = null;
  public aggregateIndex = 0;
  public aggregateVisibleCount = 0;

  public isLadderPairInvert: boolean = false;

  // primary key for ag-grid
  public orderId: string;

  constructor(
    /**
     * aggregateId stored in primary key field for ag-grid table definition
     */
    public aggregateId: string,
    public aggregationTrackingRate: number,
    public aggregationInvertTrackingRate: number,
    public baseCode: string,
    public nonBaseCode: string,
    public fixedCode: string,
    public product: CubiqTS.Product,
    public requestedOrderType: CubiqTS.RequestOrderType,
    public clientIntent: CubiqTS.OrderIntent,
    public isInvertedOrderFg: boolean,
    public deskId: CubiqTS.Long,
    public orderType: CubiqTS.OrderType,
    public orderState: CubiqTS.OrderState,
    public pauseState: CubiqTS.PauseState,
    public trackingSide: CubiqTS.RateSideType,
  ) {
    this.orderId = aggregateId;
  }

  public static is(limitOrder: any): limitOrder is LadderAggregate {
    return (limitOrder as LadderAggregate).isLadderAggregate === true;
  }

  public addUpdateLimitOrder(
    orderId: string,
    limitOrder: LimitOrder,
    context: AggregateServiceContext,
  ) {
    const isAdd = !this.aggregateOrderIdSet.has(orderId);

    this.aggregateOrderIdSet.add(orderId);
    if (this.updateOrderIdLimit(limitOrder.orderId)) {
      this.resetAggregateIndex(context);
    } else {
      limitOrder.aggregateIndex = this.aggregateOrderIdSet.size;
    }
    limitOrder.aggregate = this;

    if (isAdd && this.aggregateOrderIdSet.size === 2) {
      let [firstId] = this.aggregateOrderIdSet;
      let firstLimitOrder = context.get(firstId);
      firstLimitOrder.isAggregated = true;
      context.update(firstId);
    }
    limitOrder.isAggregated = this.aggregateOrderIdSet.size !== 1;
    context.update(orderId);
    this.resetAggregateValues();
  }

  public removeLimitOrder(orderId: string, context: AggregateServiceContext) {
    let limitOrder = context.get(orderId);
    this.aggregateOrderIdSet.delete(orderId);

    if (!(limitOrder instanceof LimitOrder)) {
      return;
    }

    limitOrder.isAggregated = false;
    limitOrder.aggregate = null;
    context.update(orderId);

    if (this.isEmpty()) {
      context.remove(this.aggregateId);
      return;
    } else if (this.aggregateOrderIdSet.size == 1) {
      let [firstId] = this.aggregateOrderIdSet;
      let firstLimitOrder = context.get(firstId);
      firstLimitOrder.isAggregated = false;
      this.aggregateExpanded = false;
      context.update(firstId);
    }

    this.resetAggregateValues();

    if (limitOrder.orderId.equals(this.aggregateOrderId)) {
      this.aggregateOrderId = null;
      this.resetAggregateIndex(context);
    }
  }

  private resetAggregateIndex(context: AggregateServiceContext) {
    let limitOrders = [...this.aggregateOrderIdSet].sort((idA, idB) => {
      const a = context.get(idA) as LimitOrder;
      const b = context.get(idB) as LimitOrder;
      return a.orderId.compare(b.orderId);
    });

    let lowest = context.get(limitOrders[0]);

    if (lowest instanceof LimitOrder) {
      this.updateOrderIdLimit(lowest.orderId);
    }

    limitOrders.forEach((id, i) => {
      let limitOrder = context.get(id);
      limitOrder.aggregateIndex = i + 1;
      context.update(id);
    });
  }

  public resetAggregateValues() {
    this.executedPerc = null;
    this.executedQty = null;
    this.leavesQty = null;
    this.orderQty = null;
    this.tempStale = true;
    this.usdEquivExecutedQty = null;
    this.usdEquivLeavesQty = null;
    this.usdEquivQty = null;
  }

  private updateOrderIdLimit(orderId: CubiqTS.Long): boolean {
    this.aggregateOrderId = this.aggregateOrderId ?? orderId;

    if (this.aggregateOrderId.compare(orderId) === 1) {
      this.aggregateOrderId = orderId;
      return true;
    }

    return false;
  }

  protected calculateExecutedPercentage(context: {
    mode: OrdersTableMode;
    isInverted: boolean;
    getLimitOrder: (orderId: string) => LimitOrder;
  }) {
    let calcExecutedQty = this.executedQty == null;
    let calcOrderQty = this.orderQty == null;
    let calcLeavesQty = this.leavesQty == null;

    if (calcExecutedQty || calcOrderQty || calcLeavesQty) {
      this.aggregateOrderIdSet.forEach((id) => {
        let limitOrder = context.getLimitOrder(id);

        if (calcExecutedQty) {
          this.updateQty(limitOrder, "executedQty");
        }
        if (calcOrderQty) {
          this.updateQty(limitOrder, "orderQty");
        }
        if (calcLeavesQty) {
          this.updateQty(limitOrder, "leavesQty");
        }
      });
    }
    let filled = this.executedQty;

    if (this.leavesQty != null && this.orderQty != null) {
      this.orderQty.minus(this.leavesQty);
    }

    if (filled != null && this.orderQty != null && !this.orderQty.isZero()) {
      this.executedPerc = Number(filled.dividedBy(this.orderQty)) * 100;
    }
  }

  private updateQty(limitOrder: LimitOrder, field: "executedQty" | "orderQty" | "leavesQty") {
    if (
      CubiqTS.BigNumber.isBigNumber(this[field]) &&
      CubiqTS.BigNumber.isBigNumber(limitOrder[field])
    ) {
      this[field] = this[field].plus(limitOrder[field]);
    } else if (CubiqTS.BigNumber.isBigNumber(limitOrder[field])) {
      this[field] = limitOrder[field];
    }
  }

  public getPosition(context: {
    isInverted: (limitOrder: LimitOrder | LadderAggregate) => boolean | null;
    getLimitOrder: (orderId: string) => LimitOrder;
  }): { ccy: string; amt: CubiqTS.BigNumber } {
    let ccy;
    let amt = CubiqTS.BigNumber("0");

    if (context?.isInverted(this)) {
      ccy = this.nonBaseCode;
    } else if (this.baseCode == this.fixedCode) {
      ccy = this.baseCode;
    } else {
      ccy = this.baseCode;
    }

    this.aggregateOrderIdSet.forEach((id) => {
      let limitOrder = context.getLimitOrder(id);
      amt = amt.plus(limitOrder.getPosition(context).amt);
    });

    return {
      ccy: ccy,
      amt: amt,
    };
  }

  public isEmpty(): boolean {
    return this.aggregateOrderIdSet.size === 0;
  }
}
